// The video footer's ratio, length and resolution menus (issue 366): live's options for the
// modes the draft can still use (`video-form-capabilities-provider.tsx` `collectionsForOptions`,
// `capabilities-utils.ts` `intersectObjectOptions`). Each mode's options are its block's where it
// has its own, else the row's, as the backend's `row.options(job_type)` reads them, so a value
// offered here is one the start can send.
import type {
  CatalogVideoModel,
  GenerationType,
  IntOptions,
  TextOptions,
  VideoMode,
} from "../../../api/generated/model";
import { optionModes, type Filled } from "./modes";

/** One footer menu: live's "auto" first where a mode has none, then the values all modes share. */
export interface FooterOption<T> {
  /** "auto" is offered: some mode left has none of these, so the model decides. */
  auto: boolean;
  values: T[];
  /** What the menu starts on, null for "auto". */
  initial: T | null;
}

export interface FooterOptions {
  ratio: FooterOption<string>;
  duration: FooterOption<number>;
  resolution: FooterOption<string>;
}

type Options<T> = { values: T[]; default: T } | null;

/** A mode's own options: its block's where it has them, else the row's (G6-studio-api 3). */
function modeOptions(model: CatalogVideoModel, block: VideoMode) {
  return {
    ratio: (block.takes_aspect_ratio === false
      ? null
      : (block.aspect_ratios ?? model.aspect_ratios)) as TextOptions | null,
    duration: (block.durations ?? model.durations ?? null) as IntOptions | null,
    resolution: (block.resolutions ?? model.resolutions ?? null) as TextOptions | null,
  };
}

/**
 * Live's `intersectObjectOptions` over the modes' sets: "auto" alone where none has any; else the
 * values every set holds, in the first set's order, after "auto" where some mode has none. It
 * starts on the first set's default when that survives, else the first value, else "auto".
 */
function intersect<T>(sets: Options<T>[]): FooterOption<T> {
  const present = sets.filter((set): set is NonNullable<Options<T>> => set !== null && set.values.length > 0);
  const missing = present.length < sets.length;
  const [first, ...rest] = present;
  if (first === undefined) return { auto: true, values: [], initial: null };
  const values = first.values.filter((value) => rest.every((set) => set.values.includes(value)));
  const initial = values.includes(first.default) ? first.default : (values[0] ?? null);
  // With no value shared, "auto" is the one option left, as live's empty intersection sends none.
  return { auto: missing || values.length === 0, values, initial };
}

/** The footer's three menus for the draft's model, inputs and type (issue 366). */
export function footerOptions(
  model: CatalogVideoModel,
  filled: Filled,
  offered: readonly GenerationType[],
  type: GenerationType,
): FooterOptions {
  const blocks = optionModes(model, filled, offered, type).map((block) => modeOptions(model, block));
  return {
    ratio: intersect(blocks.map((each) => each.ratio)),
    duration: intersect(blocks.map((each) => each.duration)),
    resolution: intersect(blocks.map((each) => each.resolution)),
  };
}

/**
 * The value a menu shows and sends: the draft's while the menu offers it ("auto" is null), else
 * the menu's start. The draft keeps its pick, so it comes back when the inputs offer it again.
 */
export function chosen<T>(option: FooterOption<T>, held: T | null): T | null {
  if (held === null) return option.auto ? null : option.initial;
  return option.values.includes(held) ? held : option.initial;
}
